import { useAppDispatch, useAppSelector } from 'app/state'
import { RequestStatus } from 'features/common'
import { useCallback, useEffect, useState } from 'react'
import { exportingJobFilter } from '../../state/jobsExportingSagas'
import {
  areExportingJobsInProgress,
  clearExportingJobsRequest,
  getExportingJobsRequest,
  setCurrentExportingJob
} from '../../state/jobsExportingSlice'
import { JobStatusType, ProcessingJob } from '../../types'
import { subDays } from 'date-fns'

function useExportingJobs() {
  const dispatch = useAppDispatch()
  const userMe = useAppSelector((state) => state.users.me)
  const exportingJobsStatus = useAppSelector((state) => state.jobsExporting.exportingJobsStatus)
  const exportingJobs = useAppSelector((state) => state.jobsExporting.exportingJobs)
  const areAnyExportingJobsRunning = useAppSelector(areExportingJobsInProgress)
  const currentExportingJob = useAppSelector((state) => state.jobsExporting.currentExportingJob)

  const [filteredExportingJobs, setFilteredExportingJobs] = useState<ProcessingJob[]>([])
  const [clearableJobs, setClearableJobs] = useState<ProcessingJob[]>([])

  const clearExportingJobs = useCallback(() => {
    const filtered = exportingJobs.filter(exportingJobFilter)
    const clearable = filtered.filter(
      (job) => job.status && [JobStatusType.Failure, JobStatusType.Success].includes(job.status.status)
    )
    setFilteredExportingJobs(filtered)
    setClearableJobs(clearable)
  }, [exportingJobs])

  const clearCurrentExportingJob = useCallback(() => {
    dispatch(setCurrentExportingJob())
  }, [dispatch])

  useEffect(() => {
    if (exportingJobsStatus === RequestStatus.Idle) {
      dispatch(
        getExportingJobsRequest({
          owner: userMe?.user_id,
          createdSince: subDays(new Date(), 7).toString()
        })
      )
    }
  }, [exportingJobsStatus, userMe, dispatch])

  useEffect(() => {
    clearExportingJobs()
  }, [clearExportingJobs, exportingJobs, dispatch])

  useEffect(() => {
    return () => {
      clearCurrentExportingJob()
    }
  }, [clearCurrentExportingJob])

  const clearJobs = () => {
    dispatch(clearExportingJobsRequest(clearableJobs))
  }

  return {
    exportingJobs,
    areAnyExportingJobsRunning,
    exportingJobsNotCleared: filteredExportingJobs,
    clearJobs,
    clearCurrentExportingJob,
    clearableJobs,
    currentExportingJob
  }
}

export default useExportingJobs
